import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Layout from '../../components/Layout';
import Container from '../../components/Container';
import Footer from '../../components/Footer';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const DEFAULT_VERSION = '1.1.2';

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
  padding-bottom: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #fff;
  text-shadow: 0 0 16px #6ea8ff, 0 2px 10px rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 6rem 1.5rem 4rem 1.5rem;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 4rem 1.5rem 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 1rem 2rem 1rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
  background: rgba(20, 30, 60, 0.7);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(45, 108, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 20px 40px rgba(45, 108, 255, 0.2),
      0 8px 16px rgba(45, 108, 255, 0.1);
    border-color: rgba(45, 108, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1.2rem;
    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const SectionTitle = styled.div`
  padding: 1.5rem;
  font-weight: 700;
  font-size: 1.15rem;
  color: #fff;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.2) 0%, rgba(58, 143, 255, 0.1) 100%);
  border-bottom: 1px solid rgba(45, 108, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #6ea8ff 0%, #3a8fff 100%);
    border-radius: 2px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const SectionContent = styled.div`
  padding: 1.5rem;
  color: #b6d0ff;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
  
  p {
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  a {
    color: #6ea8ff;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      text-decoration: underline;
      color: #3a8fff;
    }
  }
  
  ul, ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    
    li {
      margin-bottom: 0.5rem;
      
      strong {
        color: #fff;
      }
    }
  }

  code {
    background: rgba(22, 32, 74, 0.8);
    color: #b6d0ff;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: monospace;
    border: 1px solid rgba(45, 108, 255, 0.2);
  }
`;

const InstallMacOS = () => {
  const [latestVersion, setLatestVersion] = useState(DEFAULT_VERSION);
  const [loadingVersion, setLoadingVersion] = useState(true);

  useEffect(() => {
    async function fetchLatestVersion() {
      try {
        const response = await fetch('https://api.github.com/repos/deservedd/deservedwarmer/releases/latest');
        if (response.ok) {
          const data = await response.json();
          let version = data.tag_name || DEFAULT_VERSION;
          if (version.startsWith('v')) version = version.substring(1);
          setLatestVersion(version);
        } else {
          setLatestVersion(DEFAULT_VERSION);
        }
      } catch (e) {
        setLatestVersion(DEFAULT_VERSION);
      } finally {
        setLoadingVersion(false);
      }
    }
    fetchLatestVersion();
  }, []);

  return (
    <Layout>
      <PageWrapper>
        <BackgroundPattern />
        <Container>
          <ContentContainer>
            <PageTitle>macOS Installation Guide</PageTitle>
            
            <Section>
              <SectionTitle>System Requirements</SectionTitle>
              <SectionContent>
                <ul>
                  <li><strong>Operating System:</strong> macOS 10.15 (Catalina) or newer</li>
                  <li><strong>RAM:</strong> 8GB minimum (16GB recommended)</li>
                  <li><strong>Storage:</strong> 500MB free space</li>
                  <li><strong>Additional Requirements:</strong> AdsPower installed and running</li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>Download & Installation</SectionTitle>
              <SectionContent>
                <ol>
                  <li>
                    <strong>Choose Correct Version</strong>
                    {loadingVersion ? (
                      <p>Loading latest download links...</p>
                    ) : (
                      <ul>
                        <li>M1/M2 Mac: <a href={`https://github.com/deservedd/deservedwarmer/releases/download/v${latestVersion}/Account-Warmer-${latestVersion}-arm64.dmg`} target="_blank" rel="noopener">Account-Warmer-{latestVersion}-arm64.dmg</a></li>
                        <li>Intel Mac: <a href={`https://github.com/deservedd/deservedwarmer/releases/download/v${latestVersion}/Account-Warmer-${latestVersion}.dmg`} target="_blank" rel="noopener">Account-Warmer-{latestVersion}.dmg</a></li>
                      </ul>
                    )}
                  </li>
                  <li>
                    <strong>Open the DMG File</strong>
                    <p>Double-click the downloaded .dmg file</p>
                  </li>
                  <li>
                    <strong>Install to Applications</strong>
                    <p>Drag Account Warmer to your Applications folder</p>
                  </li>
                </ol>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>First Launch Steps</SectionTitle>
              <SectionContent>
                <ol>
                  <li>
                    <strong>Open the Application</strong>
                    <p>Right-click the app and select 'Open'</p>
                  </li>
                  <li>
                    <strong>Security Dialog</strong>
                    <p>Click 'Open' in the security dialog</p>
                  </li>
                  <li>
                    <strong>System Settings</strong>
                    <p>If blocked, go to System Settings &gt; Privacy &amp; Security</p>
                  </li>
                  <li>
                    <strong>Allow Anyway</strong>
                    <p>Click 'Open Anyway' to allow the application</p>
                  </li>
                </ol>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>Fix "App is Damaged" Error</SectionTitle>
              <SectionContent>
                <ol>
                  <li>
                    <strong>Open Terminal</strong>
                    <p>Launch Terminal from Applications &gt; Utilities</p>
                  </li>
                  <li>
                    <strong>Run Command</strong>
                    <p>Copy and paste this command:</p>
                    <code>sudo xattr -cr /Applications/Account\ Warmer.app</code>
                  </li>
                  <li>
                    <strong>Enter Password</strong>
                    <p>Type your administrator password when prompted</p>
                  </li>
                  <li>
                    <strong>Try Again</strong>
                    <p>Launch the application again</p>
                  </li>
                </ol>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>Need Help?</SectionTitle>
              <SectionContent>
                <p>If you encounter any issues during installation, please contact our support team:</p>
                <ul>
                  <li>
                    <a href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">@deserved0</a>
                  </li>
                  <li>
                    <a href="https://t.me/tomnookbank" target="_blank" rel="noopener noreferrer">@tomnookbank</a>
                  </li>
                </ul>
              </SectionContent>
            </Section>
          </ContentContainer>
        </Container>
        <Footer />
      </PageWrapper>
    </Layout>
  );
};

export default InstallMacOS; 